export const EVENT_ELECTRICITY_JOURNEY = 'EVENT_ELECTRIC_METER_ORDER';

const EVENT_HISTORY_METADATA_FIELDS = new Set(['user_id', 'user_display_name']);
const EVENT_METER_POSITION_FIELDS = new Set([
  'meter_position',
  'meter_position_address_key',
]);

function isHistoryObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function hasSameHistoryValue(left, right) {
  if (left === right) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((value, index) => hasSameHistoryValue(value, right[index]))
    );
  }

  if (!isHistoryObject(left) || !isHistoryObject(right)) {
    return false;
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        hasSameHistoryValue(left[key], right[key])
    )
  );
}

function cloneHistoryValue(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function hasOnlyMeterPositionChanged(previousMeter, targetMeter) {
  if (!isHistoryObject(previousMeter) || !isHistoryObject(targetMeter)) {
    return false;
  }

  const meterKeys = new Set([
    ...Object.keys(previousMeter),
    ...Object.keys(targetMeter),
  ]);

  return [...meterKeys]
    .filter((key) => !EVENT_METER_POSITION_FIELDS.has(key))
    .every((key) => hasSameHistoryValue(previousMeter[key], targetMeter[key]));
}

function isEventMeterPositionChange(previousValue, targetValue) {
  if (!isHistoryObject(previousValue) || !isHistoryObject(targetValue)) {
    return false;
  }

  const changedKeys = new Set([
    ...Object.keys(previousValue),
    ...Object.keys(targetValue),
  ]);
  if (
    [...changedKeys]
      .filter(
        (key) =>
          !EVENT_METER_POSITION_FIELDS.has(key) &&
          key !== 'additional_meters' &&
          !EVENT_HISTORY_METADATA_FIELDS.has(key)
      )
      .some((key) => !hasSameHistoryValue(previousValue[key], targetValue[key]))
  ) {
    return false;
  }

  const previousAdditionalMeters = previousValue.additional_meters ?? [];
  const targetAdditionalMeters = targetValue.additional_meters ?? [];
  if (
    !Array.isArray(previousAdditionalMeters) ||
    !Array.isArray(targetAdditionalMeters) ||
    previousAdditionalMeters.length !== targetAdditionalMeters.length ||
    !previousAdditionalMeters.every((meter, index) =>
      hasOnlyMeterPositionChanged(meter, targetAdditionalMeters[index])
    )
  ) {
    return false;
  }

  return (
    !hasSameHistoryValue(
      previousValue.meter_position,
      targetValue.meter_position
    ) ||
    previousAdditionalMeters.some(
      (meter, index) =>
        !hasSameHistoryValue(
          meter.meter_position,
          targetAdditionalMeters[index].meter_position
        )
    )
  );
}

function toEventMeterPositionHistoryValue(value, comparisonValue) {
  const meters = [];
  if (
    !hasSameHistoryValue(value.meter_position, comparisonValue.meter_position)
  ) {
    meters.push({
      meter_number: 1,
      meter_position: value.meter_position,
    });
  }

  (value.additional_meters ?? []).reduce((changedMeters, meter, index) => {
    if (
      !hasSameHistoryValue(
        meter.meter_position,
        comparisonValue.additional_meters[index].meter_position
      )
    ) {
      changedMeters.push({
        meter_number: index + 2,
        meter_position: meter.meter_position,
      });
    }
    return changedMeters;
  }, meters);

  return { event_meter_position_history: meters };
}

const EVENT_METER_POWER_FIELDS = [
  'simultaneously_required_kw',
  'expected_annual_consumption_kwh',
];

const EVENT_METER_COUNTER_FIELDS = ['counter_id', 'picture'];

const EVENT_CONNECTION_ADDRESS_FIELDS = new Set([
  'connection_address',
  'connection_address_locator',
]);

const EVENT_CONNECTION_ADDRESS_VALUE_FIELDS = [
  'connection_address',
  'connection_address_locator',
];

const EVENT_CONNECTION_ADDRESS_COMPANION_FIELDS = new Set([
  'meter_position',
  'meter_position_address_key',
  'meter_position_confirmed',
]);

function preserveUnchangedConnectionAddressContext(previousValue, targetValue) {
  if (!isHistoryObject(previousValue) || !isHistoryObject(targetValue)) {
    return;
  }

  const targetContainsAddressUpdate =
    EVENT_CONNECTION_ADDRESS_VALUE_FIELDS.some((key) =>
      Object.prototype.hasOwnProperty.call(targetValue, key)
    );

  if (targetContainsAddressUpdate) {
    return;
  }

  EVENT_CONNECTION_ADDRESS_FIELDS.forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(previousValue, key) &&
      !Object.prototype.hasOwnProperty.call(targetValue, key)
    ) {
      targetValue[key] = cloneHistoryValue(previousValue[key]);
    }
  });
}

function hasConnectionAddressChanged(previousMeter, targetMeter) {
  return [...EVENT_CONNECTION_ADDRESS_FIELDS].some(
    (key) => !hasSameHistoryValue(previousMeter[key], targetMeter[key])
  );
}

function hasOnlyMeterConnectionAddressChanged(previousMeter, targetMeter) {
  if (!isHistoryObject(previousMeter) || !isHistoryObject(targetMeter)) {
    return false;
  }

  const meterKeys = new Set([
    ...Object.keys(previousMeter),
    ...Object.keys(targetMeter),
  ]);

  return [...meterKeys]
    .filter(
      (key) =>
        !EVENT_CONNECTION_ADDRESS_FIELDS.has(key) &&
        !EVENT_CONNECTION_ADDRESS_COMPANION_FIELDS.has(key)
    )
    .every((key) => hasSameHistoryValue(previousMeter[key], targetMeter[key]));
}

function isEventConnectionAddressChange(previousValue, targetValue) {
  if (!isHistoryObject(previousValue) || !isHistoryObject(targetValue)) {
    return false;
  }

  const changedKeys = new Set([
    ...Object.keys(previousValue),
    ...Object.keys(targetValue),
  ]);
  if (
    [...changedKeys]
      .filter(
        (key) =>
          !EVENT_CONNECTION_ADDRESS_FIELDS.has(key) &&
          !EVENT_CONNECTION_ADDRESS_COMPANION_FIELDS.has(key) &&
          key !== 'additional_meters' &&
          !EVENT_HISTORY_METADATA_FIELDS.has(key) &&
          key !== 'has_existing_connection'
      )
      .some((key) => !hasSameHistoryValue(previousValue[key], targetValue[key]))
  ) {
    return false;
  }

  const previousAdditionalMeters = previousValue.additional_meters ?? [];
  const targetAdditionalMeters = targetValue.additional_meters ?? [];
  if (
    !Array.isArray(previousAdditionalMeters) ||
    !Array.isArray(targetAdditionalMeters) ||
    previousAdditionalMeters.length !== targetAdditionalMeters.length ||
    !previousAdditionalMeters.every((meter, index) =>
      hasOnlyMeterConnectionAddressChanged(meter, targetAdditionalMeters[index])
    )
  ) {
    return false;
  }

  return (
    hasConnectionAddressChanged(previousValue, targetValue) ||
    previousAdditionalMeters.some((meter, index) =>
      hasConnectionAddressChanged(meter, targetAdditionalMeters[index])
    )
  );
}

function toEventConnectionAddressHistoryValue(value) {
  const meters = [value, ...(value.additional_meters ?? [])]
    .map((meter, index) => {
      const address =
        meter.connection_address_locator ?? meter.connection_address ?? null;

      return address
        ? {
            meter_number: index + 1,
            connection_address_locator: cloneHistoryValue(address),
          }
        : null;
    })
    .filter(Boolean);

  return { event_connection_address_history: meters };
}

function hasOnlyMeterPowerChanged(previousMeter, targetMeter) {
  if (!isHistoryObject(previousMeter) || !isHistoryObject(targetMeter)) {
    return false;
  }

  const meterKeys = new Set([
    ...Object.keys(previousMeter),
    ...Object.keys(targetMeter),
  ]);

  return [...meterKeys]
    .filter((key) => !EVENT_METER_POWER_FIELDS.includes(key))
    .every((key) => hasSameHistoryValue(previousMeter[key], targetMeter[key]));
}

function isEventMeterPowerChange(previousValue, targetValue) {
  if (!isHistoryObject(previousValue) || !isHistoryObject(targetValue)) {
    return false;
  }

  const changedKeys = new Set([
    ...Object.keys(previousValue),
    ...Object.keys(targetValue),
  ]);
  if (
    [...changedKeys]
      .filter(
        (key) =>
          key !== 'additional_meters' &&
          !EVENT_HISTORY_METADATA_FIELDS.has(key) &&
          !EVENT_METER_POWER_FIELDS.includes(key)
      )
      .some((key) => !hasSameHistoryValue(previousValue[key], targetValue[key]))
  ) {
    return false;
  }

  const previousAdditionalMeters = previousValue.additional_meters ?? [];
  const targetAdditionalMeters = targetValue.additional_meters ?? [];
  if (
    !Array.isArray(previousAdditionalMeters) ||
    !Array.isArray(targetAdditionalMeters) ||
    previousAdditionalMeters.length !== targetAdditionalMeters.length ||
    !previousAdditionalMeters.every((meter, index) =>
      hasOnlyMeterPowerChanged(meter, targetAdditionalMeters[index])
    )
  ) {
    return false;
  }

  return (
    EVENT_METER_POWER_FIELDS.some(
      (field) => !hasSameHistoryValue(previousValue[field], targetValue[field])
    ) ||
    previousAdditionalMeters.some((meter, index) =>
      EVENT_METER_POWER_FIELDS.some(
        (field) =>
          !hasSameHistoryValue(
            meter[field],
            targetAdditionalMeters[index][field]
          )
      )
    )
  );
}

function toEventMeterPowerHistoryValue(value, comparisonValue) {
  const meters = [];
  const addMeterPowerValues = (meter, comparisonMeter, meterNumber) => {
    const changedValues = EVENT_METER_POWER_FIELDS.reduce(
      (result, field) => {
        if (!hasSameHistoryValue(meter[field], comparisonMeter[field])) {
          result[field] = meter[field];
        }
        return result;
      },
      { meter_number: meterNumber }
    );

    if (Object.keys(changedValues).length > 1) {
      meters.push(changedValues);
    }
  };

  addMeterPowerValues(value, comparisonValue, 1);
  (value.additional_meters ?? []).forEach((meter, index) => {
    addMeterPowerValues(
      meter,
      comparisonValue.additional_meters[index],
      index + 2
    );
  });

  return { event_meter_power_history: meters };
}

function hasOnlyMeterCounterDataChanged(previousMeter, targetMeter) {
  if (!isHistoryObject(previousMeter) || !isHistoryObject(targetMeter)) {
    return false;
  }

  const meterKeys = new Set([
    ...Object.keys(previousMeter),
    ...Object.keys(targetMeter),
  ]);

  return [...meterKeys]
    .filter((key) => !EVENT_METER_COUNTER_FIELDS.includes(key))
    .every((key) => hasSameHistoryValue(previousMeter[key], targetMeter[key]));
}

function isEventMeterCounterDataChange(previousValue, targetValue) {
  if (!isHistoryObject(previousValue) || !isHistoryObject(targetValue)) {
    return false;
  }

  const changedKeys = new Set([
    ...Object.keys(previousValue),
    ...Object.keys(targetValue),
  ]);
  if (
    [...changedKeys]
      .filter(
        (key) =>
          key !== 'additional_meters' &&
          !EVENT_HISTORY_METADATA_FIELDS.has(key) &&
          !EVENT_METER_COUNTER_FIELDS.includes(key)
      )
      .some((key) => !hasSameHistoryValue(previousValue[key], targetValue[key]))
  ) {
    return false;
  }

  const previousAdditionalMeters = previousValue.additional_meters ?? [];
  const targetAdditionalMeters = targetValue.additional_meters ?? [];
  if (
    !Array.isArray(previousAdditionalMeters) ||
    !Array.isArray(targetAdditionalMeters) ||
    previousAdditionalMeters.length !== targetAdditionalMeters.length ||
    !previousAdditionalMeters.every((meter, index) =>
      hasOnlyMeterCounterDataChanged(meter, targetAdditionalMeters[index])
    )
  ) {
    return false;
  }

  return (
    EVENT_METER_COUNTER_FIELDS.some(
      (field) => !hasSameHistoryValue(previousValue[field], targetValue[field])
    ) ||
    previousAdditionalMeters.some((meter, index) =>
      EVENT_METER_COUNTER_FIELDS.some(
        (field) =>
          !hasSameHistoryValue(
            meter[field],
            targetAdditionalMeters[index][field]
          )
      )
    )
  );
}

function toEventMeterCounterHistoryValue(value, otherValue) {
  const meters = [value, ...(value.additional_meters ?? [])];
  const otherMeters = [otherValue, ...(otherValue.additional_meters ?? [])];

  return {
    event_meter_counter_history: meters.map((meter, index) => {
      const otherMeter = otherMeters[index] ?? {};
      const counterIdChanged = !hasSameHistoryValue(
        meter.counter_id,
        otherMeter.counter_id
      );
      const pictureChanged = !hasSameHistoryValue(
        meter.picture,
        otherMeter.picture
      );

      return {
        meter_number: index + 1,
        ...(counterIdChanged ? { counter_id: meter.counter_id } : {}),
        ...(pictureChanged
          ? { picture: cloneHistoryValue(meter.picture) }
          : {}),
      };
    }),
  };
}

export function isEventElectricityJourney(journeyType) {
  return journeyType === EVENT_ELECTRICITY_JOURNEY;
}

export function normalizeEventElectricityHistory(
  payload,
  {
    isCommissioningUpdate,
    removeEmptyCommissioningChanges,
    removeIdenticalValues,
  }
) {
  delete payload.old_value?.has_existing_connection;
  delete payload.new_value?.has_existing_connection;

  if (isCommissioningUpdate) {
    preserveUnchangedConnectionAddressContext(
      payload.old_value,
      payload.new_value
    );
  }

  const eventContactPersonHistory =
    isHistoryObject(payload.old_value?.contact_person) &&
    isHistoryObject(payload.new_value?.contact_person) &&
    !hasSameHistoryValue(
      payload.old_value.contact_person,
      payload.new_value.contact_person
    )
      ? {
          previous: cloneHistoryValue(payload.old_value.contact_person),
          target: cloneHistoryValue(payload.new_value.contact_person),
        }
      : null;
  const eventConnectionAddressHistory = isEventConnectionAddressChange(
    payload.old_value,
    payload.new_value
  )
    ? {
        previous: toEventConnectionAddressHistoryValue(payload.old_value),
        target: toEventConnectionAddressHistoryValue(payload.new_value),
      }
    : null;
  const eventMeterCounterHistory = isEventMeterCounterDataChange(
    payload.old_value,
    payload.new_value
  )
    ? {
        previous: toEventMeterCounterHistoryValue(
          payload.old_value,
          payload.new_value
        ),
        target: toEventMeterCounterHistoryValue(
          payload.new_value,
          payload.old_value
        ),
      }
    : null;

  if (isCommissioningUpdate && payload.old_value && payload.new_value) {
    removeEmptyCommissioningChanges(payload.old_value, payload.new_value);
    removeIdenticalValues(payload.old_value, payload.new_value);
  }

  if (eventContactPersonHistory) {
    payload.old_value = {
      event_contact_person_history: eventContactPersonHistory.previous,
    };
    payload.new_value = {
      event_contact_person_history: eventContactPersonHistory.target,
    };
  } else if (eventConnectionAddressHistory) {
    payload.old_value = eventConnectionAddressHistory.previous;
    payload.new_value = eventConnectionAddressHistory.target;
  } else if (eventMeterCounterHistory) {
    payload.old_value = eventMeterCounterHistory.previous;
    payload.new_value = eventMeterCounterHistory.target;
  } else if (isEventMeterPositionChange(payload.old_value, payload.new_value)) {
    const { old_value: previousValue, new_value: targetValue } = payload;
    payload.old_value = toEventMeterPositionHistoryValue(
      previousValue,
      targetValue
    );
    payload.new_value = toEventMeterPositionHistoryValue(
      targetValue,
      previousValue
    );
  } else if (isEventMeterPowerChange(payload.old_value, payload.new_value)) {
    const { old_value: previousValue, new_value: targetValue } = payload;
    payload.old_value = toEventMeterPowerHistoryValue(
      previousValue,
      targetValue
    );
    payload.new_value = toEventMeterPowerHistoryValue(
      targetValue,
      previousValue
    );
  }
}
